import React, { useState, useEffect, useCallback } from 'react';
import { Smartphone, KeyRound, Save, Send, Trash2, Copy, ExternalLink, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TwilioDialog = ({ campaign, open, onOpenChange }) => {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ account_sid: '', auth_token: '', phone_number: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState('');

  const webhookUrl = campaign ? `${process.env.REACT_APP_BACKEND_URL}/api/twilio/webhook/sms/${campaign.id}` : '';

  const load = useCallback(async () => {
    if (!campaign) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/campaigns/${campaign.id}/twilio`);
      setCfg(res.data);
      setForm({ account_sid: '', auth_token: '', phone_number: res.data.phone_number || '' });
    } catch {
      toast.error('No se pudo cargar la configuración de SMS');
    } finally {
      setLoading(false);
    }
  }, [campaign]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleSave = async () => {
    if (!form.account_sid || !form.auth_token || !form.phone_number) {
      toast.error('Account SID, Auth Token y número son obligatorios');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/campaigns/${campaign.id}/twilio`, form);
      toast.success(`SMS de Twilio conectado a ${campaign.name}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testTo) { toast.error('Escribe un número de destino (+593...)'); return; }
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/campaigns/${campaign.id}/twilio/test`, { to: testTo });
      data.sent ? toast.success(data.message) : toast.error(data.message);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en la prueba');
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/campaigns/${campaign.id}/twilio`);
      toast.success('Credenciales de SMS eliminadas');
      await load();
    } catch {
      toast.error('No se pudo eliminar');
    }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success('Copiado'); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="twilio-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Smartphone className="w-4 h-4 text-brand-green" />
            SMS por Twilio · {campaign?.name}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              {cfg?.connected ? (
                <Badge className="bg-brand-green/15 text-brand-green border-brand-green/30 gap-1">
                  <CheckCircle2 className="w-3 h-3" /> {cfg.status === 'live' ? 'Verificado' : 'Configurado'}
                </Badge>
              ) : (
                <Badge variant="outline" className="gap-1 text-amber-700 dark:text-amber-400 border-amber-500/40">
                  <AlertTriangle className="w-3 h-3" /> Sin configurar
                </Badge>
              )}
              {cfg?.phone_number && <span className="text-xs text-muted-foreground">{cfg.phone_number}</span>}
            </div>

            <p className="text-xs text-muted-foreground">
              Cada campaña usa su propia línea y sus propias credenciales: los datos nunca se comparten entre campañas ni entre cuentas.
            </p>

            <div className="space-y-2">
              <Label className="text-xs">Account SID</Label>
              <Input data-testid="twilio-sid" placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                value={form.account_sid} onChange={(e) => setForm({ ...form, account_sid: e.target.value })} />
              {cfg?.account_sid_masked && <p className="text-[11px] text-muted-foreground">Guardado: {cfg.account_sid_masked}</p>}
            </div>

            <div className="space-y-2">
              <Label className="text-xs flex items-center gap-1"><KeyRound className="w-3 h-3" /> Auth Token</Label>
              <Input data-testid="twilio-token" type="password" placeholder="Tu Auth Token de Twilio"
                value={form.auth_token} onChange={(e) => setForm({ ...form, auth_token: e.target.value })} />
              {cfg?.auth_token_masked && <p className="text-[11px] text-muted-foreground">Guardado: {cfg.auth_token_masked}</p>}
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Número de Twilio (E.164)</Label>
              <Input data-testid="twilio-phone" placeholder="+13055551234"
                value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} />
            </div>

            <div className="p-3 rounded-2xl bg-muted/30 space-y-1">
              <p className="text-xs font-medium">Webhook para Twilio (A message comes in)</p>
              <div className="flex items-center gap-2">
                <code className="text-[11px] break-all flex-1 text-muted-foreground">{webhookUrl}</code>
                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => copy(webhookUrl)} data-testid="twilio-copy-webhook">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
              <a href="https://console.twilio.com/us1/develop/phone-numbers/manage/incoming" target="_blank" rel="noreferrer"
                className="text-[11px] text-primary hover:underline inline-flex items-center gap-1">
                Abrir consola de Twilio <ExternalLink className="w-3 h-3" />
              </a>
            </div>

            {cfg?.connected && (
              <div className="space-y-2">
                <Label className="text-xs">Enviar SMS de prueba a</Label>
                <div className="flex gap-2">
                  <Input data-testid="twilio-test-to" placeholder="+593..." value={testTo} onChange={(e) => setTestTo(e.target.value)} />
                  <Button variant="outline" onClick={handleTest} disabled={testing} data-testid="twilio-test-btn" className="gap-1 shrink-0">
                    {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Probar
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {cfg?.connected && (
            <Button variant="ghost" onClick={handleDelete} data-testid="twilio-delete" className="text-destructive gap-1 sm:mr-auto">
              <Trash2 className="w-4 h-4" /> Quitar
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving} data-testid="twilio-save" className="gap-1 w-full sm:w-auto">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar credenciales
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TwilioDialog;
